import axios from 'axios'
import {
  getApiBaseUrl,
  getApiTimeoutMs,
  getAuthHeaderName,
  getAuthHeaderPrefix,
  isApiUseRelative,
  isCredentialsRequired,
} from '../../src/config/api/env.js'
import { authNavigate } from '../../src/config/api/authNavigate.js'
import { getAccessToken, runRefreshHandler, setAccessToken } from '../../src/config/api/authStore.js'
import { isAuthEnforced } from '../../src/config/api/env.js'
import { reportError } from '../../src/observability/errorReporter.js'
import PATHS from '../../src/routes/paths.js'

const explicitBase = getApiBaseUrl()
const baseURL = explicitBase.length > 0 ? explicitBase : isApiUseRelative() ? '' : ''

const axiosClient = axios.create({
  baseURL,
  timeout: getApiTimeoutMs(),
  withCredentials: isCredentialsRequired(),
})

function generateRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function attachHeader(config, name, value) {
  const headers = config.headers
  if (headers && typeof headers.set === 'function') {
    headers.set(name, value)
  } else {
    config.headers = { ...config.headers, [name]: value }
  }
}

function readHeader(config, name) {
  const headers = config?.headers
  if (!headers) return undefined
  if (typeof headers.get === 'function') return headers.get(name)
  return headers[name]
}

axiosClient.interceptors.request.use((config) => {
  if (!readHeader(config, 'X-Request-Id')) {
    attachHeader(config, 'X-Request-Id', generateRequestId())
  }
  const token = getAccessToken()
  if (token) {
    const headerName = getAuthHeaderName()
    const prefix = getAuthHeaderPrefix()
    if (!readHeader(config, headerName)) {
      attachHeader(config, headerName, `${prefix}${token}`)
    }
  }
  return config
})

function tagsFromError(error) {
  return {
    requestId: readHeader(error?.config, 'X-Request-Id'),
    url: error?.config?.url,
    method: error?.config?.method,
    status: error?.response?.status,
  }
}

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }

    if (!error?.response || error.response.status !== 401) {
      reportError(error, { source: 'network', tags: tagsFromError(error) })
      return Promise.reject(error)
    }

    const original = error.config
    if (!original || original.__siaRetried) {
      reportError(error, { source: 'network', tags: tagsFromError(error) })
      return Promise.reject(error)
    }

    const next = await runRefreshHandler()
    if (!next) {
      setAccessToken(null)
      try {
        const { clearAuthSession } = await import('../../src/services/auth/processAuthSession.js')
        clearAuthSession()
      } catch {
        /* session module optional during tests */
      }
      if (isAuthEnforced()) {
        authNavigate(PATHS.SIGN_IN, { replace: true })
      }
      reportError(error, { source: 'network', tags: tagsFromError(error) })
      return Promise.reject(error)
    }

    original.__siaRetried = true
    attachHeader(original, getAuthHeaderName(), `${getAuthHeaderPrefix()}${next}`)
    return axiosClient.request(original)
  },
)

export default axiosClient
